// 商户后台侧边导航菜单配置（C1.3 冻结表：11 组 43 项 → 7 组 23 项）
// 数据驱动：增删叶子仅改本文件，不改 Sidebar 组件（对齐 web/admin/src/menu.tsx 模式）。
// 组清单：总览 / 审批中心 / 卡片与通道 / 持卡人 / 风控中心 / 财务中心 / 设置。
// 出组处置（freeze 裁决）：
//   - 营销中心组：PRD 明文本期不可达 → 菜单组与路由一并删除（不挂重定向）；
//   - 消息中心组：P-003 禁止作为一级菜单 → 出组，/merchant/messages 路由保留，Header 铃铛接管；
//   - 旧独立叶并入 Tab 壳：实体卡预申请/流转 → 新卡管理；通道充值/费率/费项 → 通道业务壳；
//     KYC 代填/罚款 → KYC 管理壳；用户风险 → 风险雷达壳；快捷处置 → 规则与处置壳；
//     充值配置 → 通道资金壳；用户充值 → 用户资金壳；品牌 → 账户与品牌壳；
//     Webhook/开发者文档 → API 与集成壳；邮件模板 → 通知与邮件壳；安全日志 → 安全与日志壳。
import type { ReactNode } from 'react';
import {
  LayoutDashboard, Users, Wallet, Settings,
  CreditCard, ClipboardCheck, ShieldAlert,
} from 'lucide-react';

/** 个人用户叶读权限（US-S5-08/09 · 后端 @PreAuthorize merchant.users:read 对齐，D10 禁自造码） */
const INDIVIDUALS_READ_CODES = ['merchant.users:read'] as const;

export interface LeafItem {
  label: string;
  path: string;
  badge?: number;
  /** 权限显隐：配置后仅命中任一码时渲染（未配置 = 恒显；复用既有码，禁新码 D10） */
  permissionCodes?: readonly string[];
}
export interface GroupItem {
  label: string;
  icon: ReactNode;
  leaves: LeafItem[];
}

export const MENU: GroupItem[] = [
  // 总览（1）
  {
    label: '总览',
    icon: <LayoutDashboard className="w-4 h-4" />,
    leaves: [{ label: '运营数据大屏', path: '/merchant/dashboard' }],
  },
  // 审批中心（PAGE-MCH-USER-015 · 独立一级菜单，独立于业务分组；硬约束不动）：
  // 角标 = APC-002 五类型 pending 总数（useMerchantBadges 动态计数，禁写死；失败/为 0 隐藏）
  {
    label: '审批中心',
    icon: <ClipboardCheck className="w-4 h-4" />,
    leaves: [{ label: '审批中心', path: '/merchant/approvals' }],
  },
  // 卡片与通道（4）：卡片域三叶 + 通道业务壳（topup in-flight 角标挂壳基路径）
  {
    label: '卡片与通道',
    icon: <CreditCard className="w-4 h-4" />,
    leaves: [
      { label: '卡片列表', path: '/merchant/card/list' },
      // 新卡管理（PAGE-MCH-CARD-014 既有壳）：实体卡预申请 / 实体卡流转两 Tab 并入（freeze 表）
      { label: '新卡管理', path: '/merchant/card/issue-center' },
      { label: '交易管理', path: '/merchant/card/transaction-center' },
      { label: '通道业务', path: '/merchant/channel' },
    ],
  },
  // 持卡人（5）：KYC 代填 / 罚款记录两叶并入 KYC 管理壳（?tab=fill|fines）
  {
    label: '持卡人',
    icon: <Users className="w-4 h-4" />,
    leaves: [
      { label: '用户管理', path: '/merchant/users' },
      { label: '邀请链接', path: '/merchant/users/invites' },
      { label: 'KYC 管理', path: '/merchant/kyc' },
      // V7.3 T1-5 / pages.md §2.7~§2.12：企业用户菜单归属用户中心；子页经详情页内子导航互达
      { label: '企业用户', path: '/merchant/users/corporates' },
      // S5 · US-S5-08：叶显隐 = merchant.users:read（AUTH-004/US-S5 例外保留，freeze 裁决 3）
      { label: '个人用户', path: '/merchant/users/individuals', permissionCodes: INDIVIDUALS_READ_CODES },
    ],
  },
  // 风控中心（2）：用户风险并入风险雷达壳（?tab=users）；快捷处置并入规则与处置壳
  // （?tab=actions，Tab 级 QUICK_ACTION_READ_CODES 过滤，AUTH-004 例外移至 Tab 级）
  {
    label: '风控中心',
    icon: <ShieldAlert className="w-4 h-4" />,
    leaves: [
      { label: '风险雷达', path: '/merchant/risk/radar' },
      { label: '规则与处置', path: '/merchant/risk/rules' },
    ],
  },
  // 财务中心（5）：充值配置并入通道资金壳；用户充值并入用户资金壳；
  // 佣金管理为既有 3-Tab 壳（三条路由 /merchant/finance/{commissions,settlements,invoices} 不变）
  {
    label: '财务中心',
    icon: <Wallet className="w-4 h-4" />,
    leaves: [
      { label: '通道资金', path: '/merchant/finance/channel' },
      { label: '用户资金', path: '/merchant/finance/users' },
      { label: '佣金管理', path: '/merchant/finance/commissions' },
      { label: '实时收支', path: '/merchant/finance/pnl' },
      { label: '划转记录', path: '/merchant/finance/credits' },
    ],
  },
  // 设置（5）：品牌 → 账户与品牌壳；Webhook/开发者文档 → API 与集成壳；
  // 邮件模板 → 通知与邮件壳；安全日志 → 安全与日志壳
  {
    label: '设置',
    icon: <Settings className="w-4 h-4" />,
    leaves: [
      { label: '账户与品牌', path: '/merchant/settings/account' },
      { label: 'API 与集成', path: '/merchant/settings/api-keys' },
      { label: '成员管理', path: '/merchant/settings/members' },
      { label: '通知与邮件', path: '/merchant/settings/email-channels' },
      { label: '安全与日志', path: '/merchant/settings/security' },
    ],
  },
];
